import { useCallback, useEffect, useRef, useState } from 'react'

import { BRUSH } from 'Pods/ThreeDStudio/constants'

// Semi-transparent brand overlay painted as the inpainting mask. Lives here (a JS
// canvas color string) rather than in SCSS, which cannot reach the 2D context.
const MASK_COLOR = 'rgba(255, 142, 0, 0.5)'

// Caps the snapshot stacks so a long editing session cannot grow memory without
// bound; the oldest action is dropped once the limit is reached.
const MAX_HISTORY = 30

/**
 * Owns the mask-painting canvas: pointer drawing, the brush/eraser tools, and an
 * undo/redo history of per-action snapshots. An "action" is one pointer-down ->
 * move -> pointer-up; its result is committed as a single snapshot on pointer-up.
 *
 * Snapshots are ImageData restored with putImageData (synchronous, no flicker).
 * The undo stack holds the canvas state after each committed action, so the live
 * canvas always matches its top; undoing past the first action clears the canvas.
 */
export default function useMaskCanvas() {
  const canvasRef = useRef(null)
  const containerRef = useRef(null)

  const undoStackRef = useRef([])
  const redoStackRef = useRef([])
  const isDrawingRef = useRef(false)
  const lastPointRef = useRef(null)

  const [brushSize, setBrushSize] = useState(BRUSH.DEFAULT)
  const [isErasing, setIsErasing] = useState(false)
  const [canUndo, setCanUndo] = useState(false)
  const [canRedo, setCanRedo] = useState(false)

  // Read inside pointer handlers so a stroke always uses the latest tool values
  // without re-binding the handlers.
  const brushSizeRef = useRef(brushSize)
  brushSizeRef.current = brushSize
  const isErasingRef = useRef(isErasing)
  isErasingRef.current = isErasing

  const syncStacks = useCallback(() => {
    setCanUndo(undoStackRef.current.length > 0)
    setCanRedo(redoStackRef.current.length > 0)
  }, [])

  const getContext = () => canvasRef.current?.getContext('2d') || null

  const restore = useCallback((snapshot) => {
    const canvas = canvasRef.current
    const ctx = getContext()
    if (!canvas || !ctx) {
      return
    }
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    if (snapshot) {
      ctx.putImageData(snapshot, 0, 0)
    }
  }, [])

  // Re-fit the backing store to the rendered box, scaling any existing mask so it
  // stays aligned with the image. The size-dependent snapshots are reset to a
  // single rescaled baseline so undo/redo never restores mismatched dimensions.
  const fitCanvas = useCallback(() => {
    const canvas = canvasRef.current
    const container = containerRef.current
    if (!canvas || !container) {
      return
    }
    const width = Math.round(container.clientWidth)
    const height = Math.round(container.clientHeight)
    if (!width || !height || (canvas.width === width && canvas.height === height)) {
      return
    }

    const hadContent = canvas.width > 0 && canvas.height > 0 && undoStackRef.current.length > 0
    let scaled = null
    if (hadContent) {
      scaled = document.createElement('canvas')
      scaled.width = canvas.width
      scaled.height = canvas.height
      scaled.getContext('2d').drawImage(canvas, 0, 0)
    }

    canvas.width = width
    canvas.height = height
    const ctx = getContext()
    if (scaled) {
      ctx.drawImage(scaled, 0, 0, width, height)
      undoStackRef.current = [ctx.getImageData(0, 0, width, height)]
    } else {
      undoStackRef.current = []
    }
    redoStackRef.current = []
    syncStacks()
  }, [syncStacks])

  useEffect(() => {
    fitCanvas()
    const container = containerRef.current
    if (!container || typeof ResizeObserver === 'undefined') {
      return undefined
    }
    const observer = new ResizeObserver(fitCanvas)
    observer.observe(container)
    return () => observer.disconnect()
  }, [fitCanvas])

  const toCanvasPoint = (event) => {
    const canvas = canvasRef.current
    const rect = canvas.getBoundingClientRect()
    return {
      x: ((event.clientX - rect.left) / rect.width) * canvas.width,
      y: ((event.clientY - rect.top) / rect.height) * canvas.height,
    }
  }

  const strokeTo = (ctx, point) => {
    const from = lastPointRef.current
    ctx.beginPath()
    ctx.moveTo(from.x, from.y)
    ctx.lineTo(point.x, point.y)
    ctx.stroke()
    lastPointRef.current = point
  }

  const handlePointerDown = useCallback((event) => {
    const canvas = canvasRef.current
    const ctx = getContext()
    if (!canvas || !ctx) {
      return
    }
    canvas.setPointerCapture?.(event.pointerId)
    isDrawingRef.current = true

    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.lineWidth = brushSizeRef.current
    ctx.strokeStyle = MASK_COLOR
    ctx.globalCompositeOperation = isErasingRef.current ? 'destination-out' : 'source-over'

    const point = toCanvasPoint(event)
    lastPointRef.current = point
    // A dot so a single click (no movement) still paints.
    strokeTo(ctx, point)
  }, [])

  const handlePointerMove = useCallback((event) => {
    if (!isDrawingRef.current) {
      return
    }
    const ctx = getContext()
    if (ctx) {
      strokeTo(ctx, toCanvasPoint(event))
    }
  }, [])

  const handlePointerUp = useCallback(() => {
    const canvas = canvasRef.current
    const ctx = getContext()
    if (!isDrawingRef.current || !canvas || !ctx) {
      return
    }
    isDrawingRef.current = false
    lastPointRef.current = null

    undoStackRef.current.push(ctx.getImageData(0, 0, canvas.width, canvas.height))
    if (undoStackRef.current.length > MAX_HISTORY) {
      undoStackRef.current.shift()
    }
    redoStackRef.current = []
    syncStacks()
  }, [syncStacks])

  const undo = useCallback(() => {
    if (undoStackRef.current.length === 0) {
      return
    }
    redoStackRef.current.push(undoStackRef.current.pop())
    restore(undoStackRef.current[undoStackRef.current.length - 1] || null)
    syncStacks()
  }, [restore, syncStacks])

  const redo = useCallback(() => {
    if (redoStackRef.current.length === 0) {
      return
    }
    const snapshot = redoStackRef.current.pop()
    undoStackRef.current.push(snapshot)
    restore(snapshot)
    syncStacks()
  }, [restore, syncStacks])

  const clear = useCallback(() => {
    undoStackRef.current = []
    redoStackRef.current = []
    isDrawingRef.current = false
    lastPointRef.current = null
    restore(null)
    syncStacks()
  }, [restore, syncStacks])

  const toggleEraser = useCallback(() => setIsErasing(prev => !prev), [])

  // Builds the "Image 2" soft-mask marker for the backend: the base image with the
  // painted regions recoloured to a red overlay, at the image's natural size so the
  // marks stay aligned. Resolves with a PNG File (or null if it can't be produced).
  const toOverlayFile = useCallback(baseImageUrl => new Promise((resolve) => {
    const maskCanvas = canvasRef.current
    if (!maskCanvas || !baseImageUrl) {
      resolve(null)
      return
    }

    const image = new Image()
    image.crossOrigin = 'anonymous'
    image.onerror = () => resolve(null)
    image.onload = () => {
      const width = image.naturalWidth || maskCanvas.width
      const height = image.naturalHeight || maskCanvas.height

      const marker = document.createElement('canvas')
      marker.width = width
      marker.height = height
      const markerCtx = marker.getContext('2d')
      markerCtx.drawImage(maskCanvas, 0, 0, width, height)
      // Keep the painted shape's alpha but recolour it red to match the prompt.
      markerCtx.globalCompositeOperation = 'source-in'
      markerCtx.fillStyle = 'rgba(255, 0, 0, 0.5)'
      markerCtx.fillRect(0, 0, width, height)

      const out = document.createElement('canvas')
      out.width = width
      out.height = height
      const ctx = out.getContext('2d')
      ctx.drawImage(image, 0, 0, width, height)
      ctx.drawImage(marker, 0, 0)

      out.toBlob(
        blob => resolve(blob ? new File([blob], 'mask-overlay.png', { type: 'image/png' }) : null),
        'image/png',
      )
    }
    image.src = baseImageUrl
  }), [])

  // Ctrl/Cmd+Z undo, Ctrl/Cmd+Shift+Z redo for keyboard users.
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== 'z') {
        return
      }
      // Don't hijack native undo/redo while the user is typing (e.g. the prompt
      // textarea); the edit canvas stays mounted across hidden tabs.
      const { target } = event
      if (target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA' || target?.isContentEditable) {
        return
      }
      event.preventDefault()
      if (event.shiftKey) {
        redo()
      } else {
        undo()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [undo, redo])

  return {
    canvasRef,
    containerRef,
    brushSize,
    setBrushSize,
    isErasing,
    toggleEraser,
    canUndo,
    canRedo,
    hasPainted: canUndo,
    undo,
    redo,
    clear,
    toOverlayFile,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
  }
}
